<template>
  <PullUpDeviceGuidePlaceHolder v-bind="$attrs">
    <template #left-icon>
      <img class="page-img" src="@assets/svgs/bg-guide-connect-confirm.svg" alt="" />
    </template>
    <template #right-content>
      <div class="page-content-text">
        <div class="page-content-model">{{ $t('owork_base.pull_device.confirm_on_device') }}</div>
        <div class="page-content-text-describe">{{ $t('owork_base.pull_device.click_on_device', { deviceName }) }}</div>
        <a-button :disabled="isCounting" class="page-content-push-btn" @click.prevent="onPushConfirm">
          {{
            isCounting
              ? $t('owork_base.pull_device.resend_notice', { count })
              : $t('owork_base.pull_device.send_notice')
          }}
        </a-button>
        <i18n-t tag="div" class="page-tips" keypath="owork_base.pull_device.tips_switch_to_password_connection_slot">
          <template #tips_switch_to_password_connection_slot_value>
            <span class="page-device-toggle-btn" @click="emits(ConnectionGuideEvents.TOGGLE_CONNECT_TYPE)">{{
              $t('owork_base.pull_device.tips_switch_to_password_connection_slot_value')
            }}</span>
          </template>
        </i18n-t>
      </div>
    </template>
  </PullUpDeviceGuidePlaceHolder>
</template>
<script lang="ts" setup>
  import { onMounted } from 'vue'
  import { INSTANCE as DevicePullUpTaskManager } from '@core/DevicePull/DevicePullUpTaskManager'
  import {
    PullUpDeviceState,
    PullUpDeviceType,
    MANUAL_CONFIRMATION_TIMEOUT_IN_SEC,
  } from '@/core/DevicePull/DevicePullUpTask'
  import type { PullUpDeviceListener } from '@core/DevicePull/DevicePullUpTask'
  import { debounce } from 'lodash'
  import { useCountdownTimer } from '@core/compositions/CountdownTimer'
  import PullUpDeviceGuidePlaceHolder from '@components/pullUpDeviceGuide/PullUpDeviceGuidePlaceHolder.vue'
  import { ConnectionGuideEvents } from '@components/Constants'
  import { TrackEventMap } from '@core/TrackConstants'
  import { usePullUpDeviceTrack } from '@core/compositions/PullUpDeviceTrack'
  import type { PullDeviceTrackInfo } from '@core/compositions/PullUpDeviceTrack'
  import { I18nT } from 'vue-i18n'

  const props = withDefaults(
    defineProps<{
      deviceId: string
      deviceName?: string
      activeConnectType: PullUpDeviceType
      needPromptEnableOWork: boolean
    }>(),
    {
      deviceName: '',
    }
  )

  const emits = defineEmits<{
    (e: ConnectionGuideEvents.TOGGLE_CONNECT_TYPE): void
  }>()
  const { count, isCounting, reset: resetCounter, start } = useCountdownTimer(MANUAL_CONFIRMATION_TIMEOUT_IN_SEC)
  const COUNTDOWN_DEBOUNCING_IN_MS = 1000

  const trackInfo: PullDeviceTrackInfo = usePullUpDeviceTrack(TrackEventMap.PULL_UP_DEVICE_BY_MANUAL_CONFIRM)
  const pullUpTaskListener: PullUpDeviceListener = {
    onPullSuccess: () => {
      trackInfo.connectSucceed = true
    },
    onPullTimeout: () => {
      trackInfo.connectSucceed = false
    },
    onPullFailed: () => {
      trackInfo.connectSucceed = false
    },
    onPullDeviceStateChange(deviceId, state) {
      switch (state) {
        case PullUpDeviceState.ON_START_PULL_UP_DEVICE:
          resetCounter()
          start()
          break
        case PullUpDeviceState.ON_STARTED_PULL_UP_DEVICE:
          trackInfo.initConnectionSucceed = true
          break
        default:
          break
      }
    },
  }

  const onPushConfirm = debounce(
    () => {
      DevicePullUpTaskManager.pullUpDevice(
        {
          deviceId: props.deviceId,
          type: PullUpDeviceType.BY_MANUAL_CONFIRM,
        },
        pullUpTaskListener,
        true,
        true,
        false
      )
    },
    COUNTDOWN_DEBOUNCING_IN_MS,
    { leading: true, trailing: false }
  )

  onMounted(() => {
    // 展示连接弹窗会先发送一次确认消息
    !props.needPromptEnableOWork && props.activeConnectType === PullUpDeviceType.BY_MANUAL_CONFIRM && onPushConfirm()
  })
</script>

<style lang="stylus" scoped>
  @import '../styles/pullUpDeviceGuide.styl'
  .page-content-text
    margin-top 132px
    .page-content-text-describe
      line-height 19.88px
    .page-tips
      line-height 17.04px

  @import '../styles/h5Adaptive/GuidePopperComponent.styl'

  @media screen and (max-width h5_width_threshold )
    .isFileManager
      .page
        .page-content
          .page-content-container
            .page-content-text
              margin-top 56px
              .page-content-text-describe
                margin-top 12px
                color h5_default_color
                font-weight h5_default_font_weight
              .page-tips
                color h5_default_shallow_color
                padding 0 16px
                text-align center
                margin-bottom 72px
</style>
